import { lazy, Suspense } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { AuthProvider } from './auth/AuthProvider.jsx';
import { RequireAuth } from './auth/RequireAuth.jsx';
import { Layout } from './components/Layout.jsx';
import { SevricLoader } from '../../chat/src/index.js';
import { RequireFeature } from './lib/useFeatures.js';

// Lazy-load every route component so the initial download is just React +
// Firebase + auth scaffolding. Each route loads its own JS chunk on first
// visit. Cuts initial bundle from ~670 KB to ~220 KB; a tech going to
// /inspect never downloads Pipeline / Admin / VehicleDetail JS.
const Pipeline      = lazy(() => import('./pages/Pipeline.jsx').then((m) => ({ default: m.Pipeline })));
const VehicleDetail = lazy(() => import('./pages/VehicleDetail.jsx').then((m) => ({ default: m.VehicleDetail })));
const Inspect       = lazy(() => import('./pages/Inspect.jsx').then((m) => ({ default: m.Inspect })));
const Admin         = lazy(() => import('./pages/Admin.jsx').then((m) => ({ default: m.Admin })));
const Login         = lazy(() => import('./pages/Login.jsx').then((m) => ({ default: m.Login })));
const Triage        = lazy(() => import('./pages/Triage.jsx').then((m) => ({ default: m.Triage })));
const Vendors       = lazy(() => import('./pages/Vendors.jsx').then((m) => ({ default: m.Vendors }))); // legacy list (kept for /vendors/classic)
const VendorDetail  = lazy(() => import('./pages/VendorDetail.jsx').then((m) => ({ default: m.VendorDetail })));
const Reports       = lazy(() => import('./pages/Reports.jsx').then((m) => ({ default: m.Reports })));
const SalespersonView = lazy(() => import('./pages/SalespersonView.jsx').then((m) => ({ default: m.SalespersonView })));
const TodayClassic    = lazy(() => import('./pages/Today.jsx'));
// ---- v3 surface (Today · Board · Units · Lot · Loads · Vendors · Flow) ----
const TodayV3         = lazy(() => import('./pages/TodayV3.jsx'));
const Board           = lazy(() => import('./pages/Board.jsx'));
const Flow            = lazy(() => import('./pages/Flow.jsx'));
const Units           = lazy(() => import('./pages/Units.jsx'));
const Lot             = lazy(() => import('./pages/Lot.jsx'));
const Loads           = lazy(() => import('./pages/Loads.jsx'));
const VendorsV3       = lazy(() => import('./pages/VendorsV3.jsx'));
const Unit            = lazy(() => import('./pages/Unit.jsx'));
const Chat            = lazy(() => import('./pages/Chat.jsx'));
const Connect         = lazy(() => import('./connect/Connect.jsx'));

// Shared boot screen used while a lazy chunk loads. Unified SEVRIC loader.
function Boot() {
  return <SevricLoader app="recon" />;
}

// Routing notes (v3):
//   /            → /today for everyone.
//   /today       Today — SEVRIC brief + numbers + board strip + feeds.
//   /board       Board (stage rail) — service-manager+. /board/settings = Flow (admin).
//   /units /lot /loads /vendors — the rest of the v3 dock. Lot is open to techs.
//   /pipeline    legacy column board (bulk move) — kept for now.
//   /dashboard   legacy widget dashboard — kept for now.
//   /vehicle/:id Same gate as /.
//   /inspect     Vehicle picker — every authenticated user.
//   /inspect/:id Per-vehicle checklist.
//   /admin       Admin only.
export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Suspense fallback={<Boot />}>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route
              element={
                <RequireAuth>
                  <Layout />
                </RequireAuth>
              }
            >
              <Route index element={<Navigate to="/today" replace />} />
              <Route
                path="pipeline"
                element={
                  <RequireAuth role="service-manager" redirectTo="/inspect">
                    <Pipeline />
                  </RequireAuth>
                }
              />
              <Route
                path="board"
                element={
                  <RequireAuth role="service-manager" redirectTo="/today">
                    <RequireFeature name="pipeline" fallback="/today"><Board /></RequireFeature>
                  </RequireAuth>
                }
              />
              <Route
                path="board/settings"
                element={
                  <RequireAuth role="admin" redirectTo="/board">
                    <Flow />
                  </RequireAuth>
                }
              />
              <Route
                path="units"
                element={
                  <RequireAuth role="service-manager" redirectTo="/today">
                    <Units />
                  </RequireAuth>
                }
              />
              <Route path="lot" element={<RequireFeature name="lot" fallback="/today"><Lot /></RequireFeature>} />
              <Route
                path="loads"
                element={
                  <RequireAuth role="service-manager" redirectTo="/today">
                    <RequireFeature name="loads" fallback="/today"><Loads /></RequireFeature>
                  </RequireAuth>
                }
              />
              <Route
                path="vehicle/:id"
                element={
                  <RequireAuth role="service-manager" redirectTo="/inspect">
                    <Unit />
                  </RequireAuth>
                }
              />
              <Route
                path="vehicle/:id/classic"
                element={
                  <RequireAuth role="service-manager" redirectTo="/inspect">
                    <VehicleDetail />
                  </RequireAuth>
                }
              />
              <Route path="inspect" element={<RequireFeature name="inspect"><Inspect /></RequireFeature>} />
              <Route path="inspect/:vehicleId" element={<RequireFeature name="inspect"><Inspect /></RequireFeature>} />
              <Route
                path="triage"
                element={
                  <RequireAuth role="service-manager" redirectTo="/inspect">
                    <RequireFeature name="triage"><Triage /></RequireFeature>
                  </RequireAuth>
                }
              />
              <Route
                path="vendors"
                element={
                  <RequireAuth role="service-manager" redirectTo="/inspect">
                    <RequireFeature name="vendors"><VendorsV3 /></RequireFeature>
                  </RequireAuth>
                }
              />
              <Route
                path="vendors/classic"
                element={
                  <RequireAuth role="service-manager" redirectTo="/inspect">
                    <RequireFeature name="vendors"><Vendors /></RequireFeature>
                  </RequireAuth>
                }
              />
              <Route
                path="vendors/:id"
                element={
                  <RequireAuth role="service-manager" redirectTo="/inspect">
                    <RequireFeature name="vendors"><VendorDetail /></RequireFeature>
                  </RequireAuth>
                }
              />
              <Route
                path="reports"
                element={
                  <RequireAuth role="service-manager" redirectTo="/inspect">
                    <RequireFeature name="reports"><Reports /></RequireFeature>
                  </RequireAuth>
                }
              />
              <Route path="today" element={<TodayV3 />} />
              <Route
                path="dashboard"
                element={
                  <RequireAuth>
                    <RequireFeature name="today"><TodayClassic /></RequireFeature>
                  </RequireAuth>
                }
              />
              <Route path="connect" element={<RequireFeature name="chat"><Connect /></RequireFeature>} />
              <Route path="connect/:roomId" element={<RequireFeature name="chat"><Connect /></RequireFeature>} />
              <Route
                path="chat"
                element={
                  <RequireAuth>
                    <RequireFeature name="chat"><Chat /></RequireFeature>
                  </RequireAuth>
                }
              />
              <Route path="v/:id" element={<RequireFeature name="salespersonView"><SalespersonView /></RequireFeature>} />
              <Route
                path="admin"
                element={
                  <RequireAuth role="admin">
                    <Admin />
                  </RequireAuth>
                }
              />
            </Route>
          </Routes>
        </Suspense>
      </AuthProvider>
    </BrowserRouter>
  );
}
